import { useSession } from '../../components/RequireAuth';

/*
 * Naming the people behind saved work, where this deployment has people.
 *
 * Here rather than in the coverage panel because three components ask it now
 * — the panel, a place's own summary, and the detail view — and a component
 * importing another component for a hook is how an import cycle starts.
 */

/**
 * Names whoever saved a plan, in the second person where it is the reader.
 *
 * "admin" twice over tells somebody nothing about which of it is theirs;
 * "You" does, at a glance, and the comparison is by username because that is
 * what the server records against the row. Matched case-insensitively: the
 * login is, and a plan saved as "Admin" is the same person.
 */
export function useWhom(): (by: string, name?: string) => string {
  const { session } = useSession();
  const me = session.username ?? '';

  return (by: string, name?: string) => {
    if (!by) return 'not recorded';
    if (me && by.toLowerCase() === me.toLowerCase()) return 'You';

    /*
      The person's name where the account has one.

      Passed in rather than looked up: the name travels with each covering,
      because the list of accounts is behind a permission a planner does not
      hold, and a screen that reads "demo_priya" to one operator and "Priya
      Raman" to another would be worse than either.
    */
    return name?.trim() || by;
  };
}

/**
 * Whether anything should be said about who did the work.
 *
 * With the login off, every saved plan is written against the one seeded
 * account — the records need a name — but there are no operators to tell
 * apart on screen. The coverage is still worth reading: these places WERE in
 * saved plans, and that is the question being asked. Who saved them is the
 * only part that stops meaning anything, so that part goes rather than
 * filling the screen with one name repeated.
 */
export function useShowPeople(): boolean {
  const { session } = useSession();

  return !session.authDisabled;
}
